import { useMemo, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AnalyticsFilterBar } from '@/components/analytics/AnalyticsFilterBar';
import { WidgetCard } from '@/components/analytics/WidgetCard';
import { KpiWithDelta } from '@/components/analytics/KpiWithDelta';
import { ExportPdfTrigger } from '@/components/analytics/ExportPdfTrigger';
import { FixtureBadge } from '@/components/analytics/FixtureBadge';
import { Chart } from '@/components/charts/Chart';
import { SectionHeader } from '@/components/analytics/SectionHeader';
import { useActiveEndpointsData } from '@/hooks/useAnalyticsData';
import { useUIStore } from '@/stores/uiStore';
import { downloadCsv } from '@/utils/csv';
import { exportFilename } from '@/utils/pdf';
import type { CategoricalDatum, SeriesDatum } from '@/components/charts/types';

/**
 * Analytics — Active Endpoint Connections (MVP, RA).
 *
 * Six widgets per `.ai/context/MVP_WIDGET_CATALOG.md` Dashboard 3:
 *   3.1 KPI w/ PoP
 *   3.2 Donut — Actively Used by Type
 *   3.3 Horizontal bar — Top 25 Buildings
 *   3.4 Line — Active Endpoints by Day
 *   3.5/3.6 Tables — Least Used / Never Used Endpoints
 *
 * Data via useActiveEndpointsData hook (which pipes filter URL state to
 * /api/analytics/active-endpoints). Backend currently in fixture mode;
 * James's RDS impl will swap in once landed (no frontend change).
 */
export function ActiveEndpoints() {
  const { data, loading, error } = useActiveEndpointsData();
  const session = useUIStore((s) => s.session);

  const byType: CategoricalDatum[] = useMemo(
    () => (data?.activelyUsedByType ?? []).map((r) => ({ label: r.label, value: r.value })),
    [data]
  );
  const topBuildings: CategoricalDatum[] = useMemo(
    () => (data?.topBuildingsByConnections ?? []).map((r) => ({ label: r.nodeName, value: r.connections })),
    [data]
  );
  const endpointsByDay: SeriesDatum[] = useMemo(
    () => (data?.activeEndpointsByDay ?? []).map((p) => ({ x: p.date, value: p.value })),
    [data]
  );

  const pdfTargetRef = useRef<HTMLDivElement>(null);

  const handleExportLeastUsedCsv = () => {
    if (!data) return;
    downloadCsv(
      data.leastUsedEndpoints.map((e) => ({
        Node: e.nodeName,
        Type: e.connectionType,
        Device: e.deviceName,
        Connection: e.connectionName,
        'Days Since Last Used': e.daysSinceLastUsed,
      })),
      exportFilename('analytics-least-used-endpoints', 'csv'),
      { report: 'Analytics — Least Used Endpoints' }
    );
  };

  const handleExportNeverUsedCsv = () => {
    if (!data) return;
    downloadCsv(
      data.neverUsedEndpoints.map((e) => ({
        Node: e.nodeName,
        Connection: e.connectionName,
        Device: e.deviceName,
        'Date Onboarded': e.dateOnboarded,
      })),
      exportFilename('analytics-never-used-endpoints', 'csv'),
      { report: 'Analytics — Never Used Endpoints' }
    );
  };

  return (
    <div className="dls-space-y-4">
      <div className="dls-flex dls-items-center dls-justify-between">
        <div>
          <h1 className="dls-text-2xl dls-font-bold dls-text-foreground">Active Endpoint Connections</h1>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2">
          <Badge variant="outline" className="dls-text-muted-foreground">Remote Access</Badge>
          <FixtureBadge />
          <ExportPdfTrigger
            title={`Active Endpoint Connections${session?.tenant?.name ? ` — ${session.tenant.name}` : ''}`}
            filenameSlug="active-endpoint-connections"
            targetRef={pdfTargetRef}
          />
        </div>
      </div>

      <AnalyticsFilterBar />

      {error && (
        <div className="dls-rounded-md dls-border dls-border-status-offline/30 dls-bg-status-offline/10 dls-px-4 dls-py-3 dls-text-sm dls-text-status-offline">
          Failed to load analytics data — {error.message}. If you just flipped <code>RA_DATALAKE_MODE=live</code> and James's RDS implementation isn't wired yet, this is expected.
        </div>
      )}

      {/* PDF capture target. */}
      <div ref={pdfTargetRef} className="dls-space-y-4">

      <SectionHeader
        title="Remote Access — Active Endpoint Connections"
        description={
          'Remote Access endpoint connectivity. "Active Connections" are those that have been used at least once; ' +
          'inactive connections (never used) are excluded from the active counts but surfaced in the Never Used ' +
          'Endpoints table below. Connection types include RDP, HTTP, HTTPS, SSH, VNC, TELNET, and TCP.'
        }
      />

      {/* 3.1 Active Connections KPI */}
      <WidgetCard
        title="Active Connections"
        info="Total number of currently-active Remote Access connections. Compared against the prior period for PoP delta."
      >
        <KpiWithDelta
          value={loading ? '—' : data?.activeConnections.current ?? 0}
          previousValue={loading ? undefined : data?.activeConnections.previousPeriod}
          deltaMode="percent"
          goodWhen="up"
          subtitle="Active Connections (PoP)"
        />
      </WidgetCard>

      <div className="dls-grid dls-grid-cols-1 lg:dls-grid-cols-2 dls-gap-4">
        {/* 3.2 Actively Used by Type — Donut */}
        <WidgetCard
          title="Actively Used Connections by Type"
          info="Breakdown of in-use connection protocols (RDP / HTTP / HTTPS / SSH / VNC / TELNET / TCP)."
        >
          {loading ? (
            <ChartLoading />
          ) : (
            <Chart type="donut" data={byType} height={280} ariaLabel="Actively used connections by type" />
          )}
        </WidgetCard>

        {/* 3.4 Active Endpoints by Day — Line */}
        <WidgetCard
          title="Active Endpoints by Day"
          info="Daily count of distinct endpoints with at least one active connection."
        >
          {loading ? (
            <ChartLoading />
          ) : (
            <Chart
              type="line"
              data={endpointsByDay}
              xKey="x"
              seriesKeys={['value']}
              height={280}
              ariaLabel="Active endpoints by day"
            />
          )}
        </WidgetCard>
      </div>

      {/* 3.3 Top Buildings by Connections */}
      <WidgetCard
        title="Buildings with Most Connections (Top 25)"
        info="Connections includes only 'Active' connections — those used at least once. Excludes never-used connections."
      >
        {loading ? (
          <ChartLoading height={400} />
        ) : (
          <Chart
            type="bar"
            data={topBuildings}
            orientation="horizontal"
            height={400}
            ariaLabel="Top buildings by connections"
          />
        )}
      </WidgetCard>

      {/* 3.5 Least Used Endpoints — Table */}
      <WidgetCard
        title="Least Used Endpoints"
        info="Endpoints ranked by days since last use (descending). Useful for cleanup audits."
        onExportCsv={data ? handleExportLeastUsedCsv : undefined}
      >
        <EndpointsTable
          loading={loading}
          rows={data?.leastUsedEndpoints ?? []}
          columns={['Node name', 'Connection type', 'Device name', 'Connection name', 'Days Since Last Used']}
          renderRow={(e) => (
            <>
              <TableCell>{e.nodeName}</TableCell>
              <TableCell>{e.connectionType}</TableCell>
              <TableCell>{e.deviceName}</TableCell>
              <TableCell>{e.connectionName}</TableCell>
              <TableCell className="dls-text-right dls-font-mono">{e.daysSinceLastUsed.toLocaleString()}</TableCell>
            </>
          )}
        />
      </WidgetCard>

      {/* 3.6 Never Used Endpoints — Table */}
      <WidgetCard
        title="Never Used Endpoints"
        info="Endpoints onboarded but never used. Strong candidates for removal."
        onExportCsv={data ? handleExportNeverUsedCsv : undefined}
      >
        <EndpointsTable
          loading={loading}
          rows={data?.neverUsedEndpoints ?? []}
          columns={['Node name', 'Connection name', 'Device name', 'Date Onboarded']}
          renderRow={(e) => (
            <>
              <TableCell>{e.nodeName}</TableCell>
              <TableCell>{e.connectionName}</TableCell>
              <TableCell>{e.deviceName}</TableCell>
              <TableCell className="dls-font-mono">{e.dateOnboarded}</TableCell>
            </>
          )}
        />
      </WidgetCard>
      </div>
    </div>
  );
}

function ChartLoading({ height = 280 }: { height?: number }) {
  return (
    <div className="dls-flex dls-items-center dls-justify-center dls-text-sm dls-text-muted-foreground" style={{ height }}>
      Loading…
    </div>
  );
}

function EndpointsTable<T>({
  loading,
  rows,
  columns,
  renderRow,
}: {
  loading: boolean;
  rows: T[];
  columns: string[];
  renderRow: (row: T) => React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="dls-py-8 dls-text-center dls-text-sm dls-text-muted-foreground">Loading…</div>
    );
  }
  if (rows.length === 0) {
    return <div className="dls-py-8 dls-text-center dls-text-sm dls-text-muted-foreground">No data</div>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((c) => (
            <TableHead key={c}>{c}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>{renderRow(row)}</TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
